import {
	addDoc,
	arrayRemove,
	arrayUnion,
	collection,
	doc,
	enableIndexedDbPersistence,
	Firestore,
	getDoc,
	getDocs,
	query,
	QuerySnapshot,
	setDoc,
	updateDoc,
	where,
	WhereFilterOp,
} from "firebase/firestore"
import { FIRESTORE_COLLECTIONS } from "../enums"
import { db } from "../firebaseApp"

interface IParams<T> {
	fieldPath: keyof T
	value: unknown
}

interface IQueryParams<T> extends IParams<T> {
	opStr: WhereFilterOp
}

export class FirestoreService {
	private readonly db: Firestore

	constructor(db: Firestore) {
		this.db = db
	}

	async init(): Promise<void> {
		await enableIndexedDbPersistence(db)
	}

	async get<T>(collectionName: FIRESTORE_COLLECTIONS, docId: string): Promise<T> {
		const docRef = doc(this.db, collectionName, docId)
		const docSnap = await getDoc(docRef)

		if (docSnap.exists()) {
			const data = docSnap.data() as T
			return { ...data, _id: docSnap.id }
		}
		throw new Error("Not found")
	}

	async query<T>(collectionName: FIRESTORE_COLLECTIONS, queryParams?: IQueryParams<T>): Promise<T[]> {
		const collectionRef = collection(this.db, collectionName)
		let querySnapshot: QuerySnapshot | undefined

		if (!queryParams) {
			querySnapshot = await getDocs(collectionRef)
		} else {
			const q = query(collectionRef, where(queryParams.fieldPath.toString(), queryParams.opStr, queryParams.value))
			querySnapshot = await getDocs(q)
		}

		return querySnapshot.docs
			.filter((doc) => doc.exists())
			.map((doc) => {
				const data = doc.data() as T
				return { ...data, _id: doc.id }
			})
	}

	async create<T>(collectionName: FIRESTORE_COLLECTIONS, payload: T, docId?: string): Promise<string> {
		if (!docId) {
			const { id } = await addDoc(collection(db, collectionName), payload)
			return id
		}

		await setDoc(doc(db, collectionName, docId), payload)
		return docId
	}

	async pushToArray<T>(collectionName: FIRESTORE_COLLECTIONS, docId: string, arrayFieldName: string, item: T) {
		const docRef = doc(this.db, collectionName, docId)
		await updateDoc(docRef, {
			[arrayFieldName]: arrayUnion(item),
		})
	}

	async removeFromArray<T>(collectionName: FIRESTORE_COLLECTIONS, docId: string, arrayFieldName: string, item: T) {
		const docRef = doc(this.db, collectionName, docId)
		await updateDoc(docRef, {
			[arrayFieldName]: arrayRemove(item),
		})
	}
}

export default new FirestoreService(db)
